import type { AnnotationLinkStyle } from 'balus-common';
import type { BezierLine } from '@model-framework/link/BezierLine/BezierLine';

const WedgeSegments = 24;
const TangentDelta = 0.01;

// 曲げハンドルを線の延長方向へ引いて曲線が途中で折り返すと、法線の向きが反転して多角形がねじれる。
// 通常の曲げ方では起きず、見た目だけの問題でデータには影響しないため受容している
const wedgeOutline = (bezier: BezierLine, halfBase: number, reversed: boolean): string | null => {
  const left: [number, number][] = [];
  const right: [number, number][] = [];
  for (let i = 0; i <= WedgeSegments; i++) {
    const t = i / WedgeSegments;
    const point = bezier.getPoint(t);
    const ahead = bezier.getPoint(Math.min(1, t + TangentDelta));
    const behind = bezier.getPoint(Math.max(0, t - TangentDelta));
    const dx = ahead.x - behind.x;
    const dy = ahead.y - behind.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) return null;

    const halfWidth = halfBase * (reversed ? t : 1 - t);
    const nx = (-dy / length) * halfWidth;
    const ny = (dx / length) * halfWidth;
    left.push([point.x + nx, point.y + ny]);
    right.push([point.x - nx, point.y - ny]);
  }
  return [...left, ...right.reverse()].map(([x, y]) => `${x},${y}`).join(' ');
};

type Props = {
  style: AnnotationLinkStyle;
  bezier: BezierLine;
  reversed: boolean;
  color: string;
  thickness: number;
};

export const AnnotationLinkShape: React.FC<Props> = ({ style, bezier, reversed, color, thickness }: Props) => {
  if (style === 'wedge') {
    const points = wedgeOutline(bezier, thickness * 4, reversed);
    return points ? <polygon points={points} fill={color} /> : null;
  }

  return (
    <path
      d={bezier.getSVGPath()}
      fill="none"
      stroke={color}
      strokeWidth={thickness}
      strokeDasharray={`0 ${thickness * 2}`}
      strokeLinecap="round"
    />
  );
};
